import { Directive, ElementRef, EventEmitter, HostListener, Inject, Input, OnInit, Output, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { NgControl, Validators, AbstractControl, ValidatorFn } from '@angular/forms';
import { parsePhoneNumberFromString } from 'libphonenumber-js';

declare const window: any;
// must match the installed intl-tel-input core version (see package.json) —
// utils.js's internal API is not guaranteed compatible across major versions,
// and a mismatch can silently break validation/formatting mid-keystroke
const defaultUtilScript = 'https://cdnjs.cloudflare.com/ajax/libs/intl-tel-input/17.0.21/js/utils.js';

//https://www.npmjs.com/package/ng2-tel-input
//https://github.com/jackocnr/intl-tel-input


@Directive({
  selector: '[countryFlagPhone]'
})
export class CountryFlagPhoneDirective implements OnInit {

  ngPhoneOptions: any = {};
  ngPhoneInput: any;
  // when set, trusted over intl-tel-input's own digit-based country detection —
  // NANP numbers (+1) are ambiguous across US/Canada/Caribbean by area code alone
  @Input() defaultCountryCode: string;
  // reports the currently-selected country whenever it's known, so a consumer
  // can save the flag the user actually saw/picked instead of re-guessing it
  // from digits alone (which is ambiguous for NANP numbers)
  @Output() countryChange = new EventEmitter<any>();

  constructor(private el: ElementRef,
    @Inject(PLATFORM_ID) private platformId: string,
    private control: NgControl) {
  }

  ngOnInit() {
    if (isPlatformBrowser(this.platformId)) {


      let valueExist = this.el.nativeElement.value;
      const knownIso2 = this.defaultCountryCode ? this.defaultCountryCode.toLowerCase() : null;

      //use for set default country flag
      this.ngPhoneOptions = {
        utilsScript: defaultUtilScript,
        // preferredCountries:["us", "in"],
        initialCountry: knownIso2 || "us",
        formatOnDisplay: false,
      }

      this.ngPhoneInput = window.intlTelInput(this.el.nativeElement, {
        ...this.ngPhoneOptions
      });

      //on flag change updating flag code value and validation — preserve any digits
      //already typed instead of collapsing the field back to the bare dial code
      //(intl-tel-input fires this while typing too, not just on manual flag pick)
      this.el.nativeElement.addEventListener("countrychange", () => {
        const telOutput = this.ngPhoneInput.getNumber();
        if (telOutput) {
          this.control.control.setValue(telOutput.indexOf('+') != -1 ? telOutput : '+' + telOutput);
        }
        // else: field is still empty — leave it that way (see ngOnInit note
        // above on why bypassing setNumber() with a bare dial code is unsafe)
        this.countryChange.emit(this.ngPhoneInput.getSelectedCountryData());
      });

      //use for if value not added in text box then flag code will add — set
      //the flag only, via intl-tel-input's own API. Writing a fake "+1"
      //straight into the control (bypassing setNumber()) desyncs intl-tel-input's
      //internal number-tracking from the DOM, which breaks its handling of the
      //very first keystrokes typed afterward. "+1" alone was never a valid
      //number anyway, so there's nothing lost by leaving it genuinely empty.
      if (!valueExist) {
        this.setCountry(knownIso2 || 'us');
      } else if (knownIso2) {
        // trust the app's own stored country over intl-tel-input's digit-based
        // guess — pass only the national number so it can't re-detect a
        // different (but dial-code-sharing) country from the full "+" string
        this.setCountry(knownIso2);
        const parsed = parsePhoneNumberFromString(valueExist);
        const nationalOnly = parsed?.nationalNumber || valueExist.replace(/^\+?\d{1,3}/, '');
        this.setNumber(nationalOnly);
        this.control.control.setValue(valueExist);
      } else {
        this.setNumber(valueExist);
        this.control.control.setValue(valueExist);
      }

      // let the consumer know the initial flag even if the user never
      // touches the dropdown (e.g. a fresh field defaulting to US)
      this.countryChange.emit(this.ngPhoneInput.getSelectedCountryData());

    }
    this.addSearchBox() 
  }


  addSearchBox() {
    const dropdown = document.querySelector('.iti__country-list');
 
    if (dropdown) {
      // Create the search input
      const searchBox = document.createElement('input');
      searchBox.setAttribute('type', 'text');
      searchBox.setAttribute('placeholder', 'Search Country');
      searchBox.classList.add('iti__search-box');
      searchBox.style.cssText = `
        width: 100%;
        padding: 6px;
        margin: 4px 0;
        border: 1px solid #ccc;
        box-sizing: border-box;
        color: rgb(204, 204, 204);
        background-color: black;
      `;
 
      // Prevent dropdown from closing on interaction
      searchBox.addEventListener('click', (e) => e.stopPropagation());
      searchBox.addEventListener('keydown', (e) => e.stopPropagation());
      searchBox.addEventListener('input', (e) => {
        e.stopPropagation(); // Prevent closing on input
 
        const searchValue = searchBox.value.toLowerCase();
        const countries = dropdown.querySelectorAll('.iti__country');
 
        // Filter countries based on input
        countries.forEach((country: HTMLElement) => {
          const countryName = country.innerText.toLowerCase();
          country.style.display = countryName.includes(searchValue) ? 'block' : 'none';
        });
      });
 
      // Insert the search box into the dropdown
      dropdown.insertBefore(searchBox, dropdown.firstChild);
    }
  }

  //on textbox blur value and validation updating
  @HostListener('blur') onBlur() {
    let isInputValid: boolean = this.isInputValid();
    if (isInputValid) {
      let telOutput = this.ngPhoneInput.getNumber();
      this.updateValidation(isInputValid);
      if (telOutput) {
        telOutput = telOutput.indexOf('+') != -1 ? telOutput : '+' + telOutput
        this.control.control.setValue(telOutput);
      }
    } else {
      this.updateValidation(isInputValid);
    }
  }

  //on text box value change updating validation and textbox value
  @HostListener('input', ['$event']) onInputChange(event) {
    // setValue() below rewrites the native input's value, which resets the
    // cursor to the end — restore the caret so editing mid-string works
    const el = this.el.nativeElement;
    const cursorPos = el.selectionStart;
    const prevLength = el.value.length;

    let isInputValid: boolean = this.isInputValid();
    if (isInputValid) {
      let telOutput = this.ngPhoneInput.getNumber();
      this.updateValidation(isInputValid);
      if (telOutput) {
        telOutput = telOutput.indexOf('+') != -1 ? telOutput : '+' + telOutput
        this.control.control.setValue(telOutput);
      }
    } else {
      this.updateValidation(isInputValid);
    }

    if (cursorPos != null) {
      const newPos = Math.max(0, cursorPos + (el.value.length - prevLength));
      requestAnimationFrame(() => el.setSelectionRange(newPos, newPos));
    }
  }

  //updating form validation
  updateValidation(isvalidNumber) {
    const validatorFn = this.control.control.validator;
    const validator = validatorFn ? validatorFn({} as AbstractControl) : null;
    if (validator && validator.required) {
      this.control.control.setValidators([Validators.required, isValiddateNumber.isvalidNumber(isvalidNumber)]);
    } else {
      // read the live DOM value, not control.control.value — Angular's own
      // sync of the FormControl from the DOM isn't guaranteed to have run
      // before this listener fires, and writing a stale control value back
      // here would overwrite whatever was just typed, every keystroke
      const domValue = this.el.nativeElement.value;
      let value = domValue ? domValue.replace(/[^0-9+]*/g, '') : "";
      this.control.control.setValue(value);
      if (value)
        this.control.control.setValidators([isValiddateNumber.isvalidNumber(isvalidNumber)]);
      else
        this.control.control.setValidators([isValiddateNumber.isvalidNumber(true)]);
    }
    this.control.control.updateValueAndValidity();
  };

  //get value status
  isInputValid(): boolean {
    return this.ngPhoneInput.isValidNumber();
  }

  //set country country code
  setCountry(country: any) {
    this.ngPhoneInput.setCountry(country);
  }

  //set phone number in text
  setNumber(valueExist: any) {
    this.ngPhoneInput.setNumber(valueExist);
  }

}


//use update control validation
export class isValiddateNumber {
  static isvalidNumber(isvalidNumber): ValidatorFn {
    return (c: AbstractControl): { [key: string]: boolean } | null => {
      let isInvalid = !isvalidNumber;
      // if (!c.value || c.value.length < 4)
      //   isInvalid = true;

      if (!isInvalid)
        return null;
      else
        return { 'isInvalid': isInvalid };
    }
  }
}
